import { match } from 'ts-pattern'
import { Icon } from '@/components'
import { Link } from '@mui/material'
import { Check as FeatherCheck } from 'react-feather'
import { AnimatePresence, motion } from 'motion/react'
import { useHistory, useLocation } from 'react-router-dom'
import TextFormField from '@/components/forms/text-form-field'
import MarketingPixelTracking from '@/components/MarketingPixelTracking/MarketingPixelTracking'
import OtpCodeEntry from './OtpCodeEntry'
import type { ProgramSignupSheetViewModel } from './useProgramSignupSheetViewModel'
import '../styles.scss'

type Props = {
  viewModel: ProgramSignupSheetViewModel
}

export default function ProgramSignupSheetContent({ viewModel }: Props) {
  return match(viewModel.mode)
    .with('signup', () => <SignupContent viewModel={viewModel} />)
    .with('login', 'impersonate', () => <LoginContent viewModel={viewModel} />)
    .with('forgotPassword', () => (
      <ForgotPasswordContent viewModel={viewModel} />
    ))
    .with('otp', () => <OtpContent viewModel={viewModel} />)
    .exhaustive()
}

function OtpContent({ viewModel }: Props) {
  const isLogin = viewModel.otpFlow === 'login'
  return (
    <OtpCodeEntry
      email={viewModel.otpEmail}
      isLogin={isLogin}
      submitLabel={isLogin ? 'Verify and Log in' : 'Verify and Sign Up'}
      loading={viewModel.loading}
      requestLoading={viewModel.otpRequestLoading}
      resendLoading={viewModel.otpRequestLoading}
      errorMessage={viewModel.otpVerifyError}
      resendConfirmationVisible={viewModel.otpResendCount > 0}
      onSubmit={viewModel.onVerifyOtp}
      onResend={viewModel.onResendOtp}
      onBack={viewModel.onBackFromOtp}
      resetSignal={viewModel.otpResetSignal}
    />
  )
}

type LoginLocationState = { signupUrl?: string }

function useLoginSignupNavigation() {
  const history = useHistory()
  const location = useLocation<LoginLocationState>()
  const { pathname } = location
  const parts = pathname.split('/').filter(Boolean)

  const navigateToLogin = () => {
    // /:utility/signup (exactly 2 parts, not a program route) → /login/:utility
    // /program/:id/signup or /signup → /login
    const isUtilitySignup =
      parts.length === 2 && parts[1] === 'signup' && parts[0] !== 'program'
    history.push(isUtilitySignup ? `/login/${parts[0]}` : '/login', {
      signupUrl: pathname,
    })
  }

  const navigateToSignup = () => {
    const signupUrl = location.state?.signupUrl
    if (signupUrl) {
      history.push(signupUrl, { openSheet: true })
      return
    }
    const utility =
      parts[0] === 'login' && parts.length > 1 ? parts[1] : undefined
    history.push(utility ? `/${utility}/signup` : '/signup', {
      openSheet: true,
    })
  }

  return { navigateToLogin, navigateToSignup }
}

function SignupContent({ viewModel }: Props) {
  const { navigateToLogin } = useLoginSignupNavigation()
  return (
    <div className="flex flex-col gap-10 font-[inter]">
      <div>
        <h2 className="program-signup-sheet__title md:text-[32px]">
          {viewModel.isGeneric ? 'Sign Up' : 'Sign Up & Apply'}
        </h2>
        {viewModel.signupSuccess && (
          <MarketingPixelTracking
            event="SIGNUP"
            subid={viewModel.signupForm.watch('referral_code')}
          />
        )}
      </div>

      <div className="flex flex-col gap-6">
        <ErrorBanner errors={viewModel.errors ?? {}} />

        <OAuthButtons
          onGoogle={viewModel.continueWithGoogle}
          onApple={viewModel.continueWithApple}
          label="Sign up"
        />

        <OrDivider />

        <form
          action="post"
          noValidate
          onSubmit={viewModel.signupForm.handleSubmit(viewModel.onSignupSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <TextFormField
                label="Full Name"
                placeholder="eg. John Smith"
                data-testing-id="signup-name"
                name="name"
                register={viewModel.signupForm.register}
                error={viewModel.getSignupFieldError('name')}
                showErrorDropdown={true}
              />
            </div>
            <div className="flex-1">
              <TextFormField
                type="email"
                label="Email"
                placeholder="you@email.com"
                data-testing-id="signup-email"
                name="username"
                register={viewModel.signupForm.register}
                error={viewModel.getSignupFieldError('username')}
                showErrorDropdown={true}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={viewModel.toggleReferralCode}
            className="text-[#3768FB] text-[14px] font-bold text-left bg-transparent border-0 cursor-pointer p-0 flex items-center gap-1 underline leading-6 font-[inter]"
          >
            View referral code?
            <Icon
              name="ChevronUp"
              size={16}
              color="#3768FB"
              className={`transition-transform ${
                viewModel.showReferralCode ? 'rotate-180' : ''
              }`}
            />
          </button>
          {viewModel.showReferralCode && (
            <TextFormField
              label="Referral Code"
              data-testing-id="referral-code"
              name="referral_code"
              register={viewModel.signupForm.register}
              disabled={Boolean(viewModel.paramReferralCode)}
            />
          )}

          <div className="flex flex-col gap-2">
            <button
              type="submit"
              disabled={
                viewModel.loading ||
                !viewModel.executeRecaptcha ||
                viewModel.captchaLoading
              }
              className={SUBMIT_BUTTON_CLASS}
            >
              {viewModel.isGeneric
                ? 'Sign Up & Start Saving'
                : `Sign Up & Apply${
                    viewModel.totalRewardsDollars > 0
                      ? `  –  Earn $${viewModel.totalRewardsDollars}+`
                      : ''
                  }`}
            </button>
            <EncryptionBadge />
          </div>
        </form>

        <p className={MODE_TOGGLE_PARAGRAPH_CLASS}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={navigateToLogin}
            className={MODE_TOGGLE_CLASS}
          >
            Log in
          </button>
        </p>
      </div>

      <div>
        <ReviewCard />
      </div>

      <div>
        <PrivacyTerms />
      </div>
    </div>
  )
}

function LoginContent({ viewModel }: Props) {
  const { navigateToSignup } = useLoginSignupNavigation()
  const isImpersonate = viewModel.mode === 'impersonate'
  return (
    <div className="flex flex-col gap-10 font-[inter]">
      <div>
        <h2 className="program-signup-sheet__title md:text-[32px]">Log in</h2>
      </div>

      <div className="flex flex-col gap-6">
        <ErrorBanner errors={viewModel.errors ?? {}} />

        {!isImpersonate && (
          <>
            <OAuthButtons
              onGoogle={viewModel.continueWithGoogle}
              onApple={viewModel.continueWithApple}
              label="Log in"
            />

            <OrDivider />
          </>
        )}

        <form
          action="post"
          noValidate
          onSubmit={viewModel.loginForm.handleSubmit(viewModel.onLoginSubmit)}
          className="flex flex-col gap-6"
        >
          <TextFormField
            type="email"
            label="Email"
            placeholder="you@email.com"
            data-testing-id="login-email"
            name="username"
            register={viewModel.loginForm.register}
            error={viewModel.getLoginFieldError('username')}
            showErrorDropdown={true}
          />
          {viewModel.loginHasPassword && (
            <>
              <TextFormField
                type="password"
                label="Password"
                placeholder="Min. 8 characters"
                data-testing-id="login-password"
                name="password"
                register={viewModel.loginForm.register}
                error={viewModel.getLoginFieldError('password')}
                showErrorDropdown={true}
              />

              {!isImpersonate && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={viewModel.onSendCodeInstead}
                    className="text-[#3768FB] text-[14px] font-bold bg-transparent border-0 cursor-pointer p-0 underline font-[inter]"
                  >
                    Email me a code instead
                  </button>
                </div>
              )}
            </>
          )}

          <div className="flex flex-col gap-2">
            <button
              type="submit"
              disabled={
                viewModel.loading ||
                viewModel.emailLookupLoading ||
                viewModel.otpRequestLoading
              }
              className={SUBMIT_BUTTON_CLASS}
            >
              {viewModel.loginHasPassword ? 'Log in' : 'Continue'}
            </button>
            <EncryptionBadge />
          </div>
        </form>

        {!isImpersonate && (
          <>
            <div className="flex flex-col gap-3">
              {viewModel.loginHasPassword && (
                <p className="text-center text-[12px] font-medium text-[#5d5d5d] leading-5 m-0">
                  <button
                    type="button"
                    onClick={() => viewModel.setMode('forgotPassword')}
                    className="text-[#3768FB] font-bold underline leading-5 bg-transparent border-0 cursor-pointer p-0 text-[14px] font-[inter]"
                  >
                    Forgot password?
                  </button>
                </p>
              )}
              <p className={MODE_TOGGLE_PARAGRAPH_CLASS}>
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={navigateToSignup}
                  className={MODE_TOGGLE_CLASS}
                >
                  Sign up
                </button>
              </p>
            </div>
          </>
        )}
      </div>

      <div>
        <ReviewCard />
      </div>

      <div>
        <PrivacyTerms />
      </div>
    </div>
  )
}

function ForgotPasswordContent({ viewModel }: Props) {
  return (
    <div className="flex flex-col gap-10 font-[inter]">
      <div>
        <h2 className="program-signup-sheet__title md:text-[32px]">
          Reset Password
        </h2>
      </div>

      <div className="flex flex-col gap-6">
        <form
          action="post"
          noValidate
          onSubmit={viewModel.forgotPasswordForm.handleSubmit(
            viewModel.onResetPassword,
          )}
          className="flex flex-col gap-6"
        >
          <AnimatePresence>
            {viewModel.passwordResetErrors &&
              Object.keys(viewModel.passwordResetErrors).length > 0 && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden text-red-600 bg-red-50 p-3 pl-4 font-semibold rounded-md text-[14px]"
                >
                  {(viewModel.passwordResetErrors?.email as string[]) ??
                    'An error occurred. Please try again.'}
                </motion.div>
              )}
          </AnimatePresence>

          <p className="text-[14px] font-medium text-[#5d5d5d] leading-5 m-0">
            Enter your email address and we&apos;ll send you a link to reset
            your password.
          </p>

          <TextFormField
            type="email"
            label="Email"
            placeholder="you@email.com"
            data-testing-id="reset-email"
            name="email"
            register={viewModel.forgotPasswordForm.register}
            error={viewModel.forgotPasswordForm.formState.errors.email?.message}
            showErrorDropdown={true}
          />

          <AnimatePresence>
            {viewModel.resetLinkSent && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-start gap-2 bg-[#E5FBEC] p-4 rounded-[10px]">
                  <FeatherCheck
                    size={20}
                    color="#00A331"
                    className="--icon shrink-0"
                  />
                  <p className="m-0 font-[inter] text-[14px] font-medium leading-5 text-[#00A331]">
                    We&apos;ve sent a password reset link to your email address.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={
              viewModel.passwordResetLoading ||
              !viewModel.forgotPasswordForm.formState.isValid
            }
            className={SUBMIT_BUTTON_CLASS}
          >
            {viewModel.passwordResetLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <p className={MODE_TOGGLE_PARAGRAPH_CLASS}>
          Remember your password?{' '}
          <button
            type="button"
            onClick={() => viewModel.setMode('login')}
            className={MODE_TOGGLE_CLASS}
          >
            Log in
          </button>
        </p>
      </div>

      <div>
        <PrivacyTerms />
      </div>
    </div>
  )
}

const SUBMIT_BUTTON_CLASS =
  'w-full bg-[#af4bfb] hover:bg-[#9a3ae6] disabled:opacity-50 text-white font-bold text-[14px] leading-5 p-5 rounded-[20px] transition-colors border-0 cursor-pointer font-[inter]'

const MODE_TOGGLE_CLASS =
  'text-[#3768FB] font-bold underline leading-6 bg-transparent border-0 cursor-pointer p-0 text-[14px] font-[inter]'

const OAUTH_BUTTON_CLASS =
  'flex-1 flex items-center justify-center gap-2 h-[48px] p-4 rounded-[8px] cursor-pointer hover:bg-gray-200 bg-[#f2f2f2] border-0 text-[#343434] font-medium text-[14px] leading-5 font-[inter]'

const MODE_TOGGLE_PARAGRAPH_CLASS =
  'text-center text-[14px] font-medium text-[#343434] leading-5 m-0'

function ErrorBanner({ errors }: { errors: Record<string, unknown> }) {
  return (
    <AnimatePresence>
      {(errors?.detail as string) && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          className="overflow-hidden text-themed-error bg-themed-error/20 p-3 pl-4 font-semibold rounded-md max-sm:text-sm"
        >
          {errors.detail as string}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function OAuthButtons({
  onGoogle,
  onApple,
  label,
}: {
  onGoogle: () => Promise<void>
  onApple: () => Promise<void>
  label: string
}) {
  return (
    <div className="flex gap-6">
      <button type="button" onClick={onGoogle} className={OAUTH_BUTTON_CLASS}>
        <Icon name="Google" size={20} />
        <span className="hidden md:inline">{label} with Google</span>
      </button>
      <button type="button" onClick={onApple} className={OAUTH_BUTTON_CLASS}>
        <Icon name="Apple" size={20} />
        <span className="hidden md:inline">{label} with Apple</span>
      </button>
    </div>
  )
}

function OrDivider() {
  return (
    <div className="flex items-center gap-4">
      <div className="flex-1 h-px bg-[#e5e5e5]" />
      <span className="font-bold text-[12px] text-[#5d5d5d] tracking-[-0.12px] leading-4">
        Or
      </span>
      <div className="flex-1 h-px bg-[#e5e5e5]" />
    </div>
  )
}

function EncryptionBadge() {
  return (
    <div className="flex items-center justify-center gap-2">
      <Icon name="Shield" size={16} color="#5d5d5d" />
      <span className="text-[12px] font-medium text-[#5d5d5d] leading-5">
        256-bit encryption
      </span>
    </div>
  )
}

function FilledStar() {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="#FBBF24"
      stroke="#FBBF24"
      strokeWidth={1}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}

function ReviewCard() {
  return (
    <div className="bg-[#f2f2f2] rounded-[20px] p-4 flex flex-col gap-4">
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <FilledStar key={i} />
        ))}
      </div>
      <p className="text-[12px] font-medium text-[#5d5d5d] leading-5 m-0">
        &ldquo;Optiwatt made it easy to enroll my vehicle in a program. After
        signing up, I just connected my car and submitted an application in
        minutes.&rdquo;
      </p>
      <div className="flex items-start gap-2 rounded-[8px]">
        <Icon name="User" size={20} color="purple-500" strokeWidth={2.25} />
        <div className="flex flex-col leading-5 text-[#5d5d5d]">
          <p className="text-[14px] font-bold m-0">James S.</p>
          <p className="text-[12px] font-medium m-0">EV owner, Los Angeles</p>
        </div>
      </div>
    </div>
  )
}

export function PrivacyTerms() {
  return (
    <p className="text-center text-[12px] font-medium text-[#5d5d5d] leading-5 m-0">
      By continuing, you agree to our{' '}
      <Link
        className="!text-[#3768FB] !underline !text-[12px] !font-medium !leading-5"
        href="/tos"
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms of service
      </Link>{' '}
      and acknowledge that you understand the{' '}
      <Link
        className="!text-[#3768FB] !underline !text-[12px] !font-medium !leading-5"
        href="/privacy"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy policy
      </Link>
      .
    </p>
  )
}
